import { useState, useRef, useEffect } from "react";
import { Tag, ChevronDown, Plus, Check, Pencil, Trash2, X } from "lucide-react";
import { clsx } from "clsx";
import {
  useCreateTaskCategory,
  useUpdateTaskCategory,
  useDeleteTaskCategory,
} from "@/features/tasks/hooks/useTaskCategories";
import type { TaskCategory } from "@/features/tasks/types";

const PRESET_COLORS = [
  "#6366f1", "#3b82f6", "#06b6d4", "#10b981", "#84cc16",
  "#f59e0b", "#ef4444", "#ec4899", "#8b5cf6", "#64748b",
];

interface Props {
  categories: TaskCategory[];
  activeFilter: string | "none" | undefined;
  onFilterChange: (val: string | "none" | undefined) => void;
}

export function CategoryFilter({ categories, activeFilter, onFilterChange }: Props) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const { mutate: createCat, isPending: creating } = useCreateTaskCategory();
  const { mutate: updateCat } = useUpdateTaskCategory();
  const { mutate: deleteCat } = useDeleteTaskCategory();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    createCat({ name, color: newColor }, {
      onSuccess: () => { setNewName(""); setNewColor(PRESET_COLORS[0]); setAdding(false); },
    });
  }

  function startEdit(cat: TaskCategory) {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
  }

  function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    const name = editName.trim();
    if (!name) return;
    updateCat({ id: editingId, name, color: editColor }, {
      onSuccess: () => setEditingId(null),
    });
  }

  const activeLabel =
    activeFilter === "none"
      ? "Bez kategorii"
      : categories.find((c) => c.id === activeFilter)?.name ?? "Kategorie";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] transition-colors",
          activeFilter !== undefined
            ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)] dark:bg-blue-900/30"
            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400",
        )}
      >
        <Tag size={13} />
        <span>{activeLabel}</span>
        <ChevronDown size={13} className="opacity-60" />
      </button>

      {open && (
        <div className="absolute left-0 top-10 z-40 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Kategorie</span>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={14} />
            </button>
          </div>

          <div className="py-1">
            <button
              onClick={() => { onFilterChange(undefined); setOpen(false); }}
              className={clsx(
                "w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50",
                activeFilter === undefined ? "text-[var(--color-primary)] font-medium" : "text-gray-700 dark:text-gray-300",
              )}
            >
              Wszystkie zadania
              {activeFilter === undefined && <Check size={13} className="ml-auto text-[var(--color-primary)]" />}
            </button>
            <button
              onClick={() => { onFilterChange("none"); setOpen(false); }}
              className={clsx(
                "w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50",
                activeFilter === "none" ? "text-[var(--color-primary)] font-medium" : "text-gray-700 dark:text-gray-300",
              )}
            >
              <span className="w-3 h-3 rounded-sm border-2 border-dashed border-gray-300 dark:border-gray-500 flex-shrink-0" />
              Bez kategorii
              {activeFilter === "none" && <Check size={13} className="ml-auto text-[var(--color-primary)]" />}
            </button>
          </div>

          {categories.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 py-1">
              {categories.map((cat) =>
                editingId === cat.id ? (
                  <form key={cat.id} onSubmit={submitEdit} className="px-3 py-2 space-y-1.5">
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      placeholder="Nazwa kategorii"
                    />
                    <div className="flex gap-1 flex-wrap">
                      {PRESET_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditColor(c)}
                          className={clsx("w-5 h-5 rounded-full transition-transform", editColor === c && "ring-2 ring-offset-1 ring-gray-400 scale-110")}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <button type="submit" className="flex-1 text-xs bg-[var(--color-primary)] text-white rounded px-2 py-1 hover:bg-[var(--color-primary-hover)]">
                        Zapisz
                      </button>
                      <button type="button" onClick={() => setEditingId(null)} className="flex-1 text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Anuluj
                      </button>
                    </div>
                  </form>
                ) : (
                  <div
                    key={cat.id}
                    className={clsx(
                      "flex items-center gap-2 px-3 py-1.5 group/cat hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer",
                      activeFilter === cat.id && "bg-gray-50 dark:bg-gray-700/50",
                    )}
                    onClick={() => { onFilterChange(cat.id); setOpen(false); }}
                  >
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <span className={clsx("text-sm flex-1 truncate", activeFilter === cat.id ? "text-[var(--color-primary)] font-medium" : "text-gray-700 dark:text-gray-300")}>
                      {cat.name}
                    </span>
                    {activeFilter === cat.id && <Check size={13} className="text-[var(--color-primary)] flex-shrink-0" />}
                    <div className="hidden group-hover/cat:flex items-center gap-1 ml-auto">
                      <button
                        onClick={(e) => { e.stopPropagation(); startEdit(cat); }}
                        className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        title="Edytuj kategorię"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`Usuń kategorię „${cat.name}"?`)) {
                            if (activeFilter === cat.id) onFilterChange(undefined);
                            deleteCat(cat.id);
                          }
                        }}
                        className="p-0.5 text-gray-400 hover:text-red-500"
                        title="Usuń kategorię"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}

          <div className="border-t border-gray-100 dark:border-gray-700">
            {adding ? (
              <form onSubmit={submitCreate} className="px-3 py-2 space-y-1.5">
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="Nazwa nowej kategorii"
                />
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewColor(c)}
                      className={clsx("w-5 h-5 rounded-full transition-transform", newColor === c && "ring-2 ring-offset-1 ring-gray-400 scale-110")}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="submit"
                    disabled={creating || !newName.trim()}
                    className="flex-1 text-xs bg-[var(--color-primary)] text-white rounded px-2 py-1 hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
                  >
                    Dodaj
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAdding(false); setNewName(""); }}
                    className="flex-1 text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <Plus size={12} />
                Nowa kategoria
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
